import apiClient from '@/api/apiClient';
import { handleError, getAuthData, prepareModel } from '../../utils/storeHelpers';

export default {
  namespaced: true,
  state: {
    schema: {},
    dataCrud: [],
    dataCrud2: [],
    dataGroupsT: [],
    dataCodeT: [],
    checkboxDS: null,
    isNewRecord: true,
    isNewRecordDS: true,
    status: '',
  },

  getters: {
    schema: (state) => state.schema,
    dataCrud: (state) => state.dataCrud,
    dataCrud2: (state) => state.dataCrud2,
    dataGroupsT: (state) => state.dataGroupsT,
    dataCodeT: (state) => state.dataCodeT,
    checkboxDS: (state) => state.checkboxDS,
    isNewRecord: (state) => state.isNewRecord,
    isNewRecordDS: (state) => state.isNewRecordDS,
    status: (state) => state.status,
  },

  mutations: {
    SET_SCHEMA(state, schema) {
      state.schema = schema;
    },
    SET_RECORDS(state, { recordsData }) {
      state.dataCrud = Array.isArray(recordsData?.dataCrud) ? recordsData.dataCrud : [];
      state.dataCrud2 = Array.isArray(recordsData?.dataCrudCT) ? recordsData.dataCrudCT : [];
      state.isNewRecord = Boolean(recordsData?.isNewRecord);
    },
    SET_RECORDS_TRACE(state, { recordsData }) {
      state.dataGroupsT = Array.isArray(recordsData?.dataGroupsT) ? recordsData.dataGroupsT : [];
      state.dataCodeT = Array.isArray(recordsData?.dataCodeT) ? recordsData.dataCodeT : [];
      state.checkboxDS = recordsData?.checkboxDS;
      state.isNewRecordDS = Boolean(recordsData?.isNewRecordDS);
    },
    SET_TRACEDIAG(state, { dataGroupsT, dataCodeT }) {
      state.dataGroupsT = Array.isArray(dataGroupsT) ? dataGroupsT : [];
      state.dataCodeT = Array.isArray(dataCodeT) ? dataCodeT : [];
    },
    SET_STATUS(state, status) {
      state.status = status;
    },
  },

  actions: {
    async fetchSchema({ commit }) {
      try {
        commit('SET_STATUS', 'loading');
        const responses = await Promise.all([
          apiClient.dsxDetailsSymptomsSchema(),
          apiClient.catDetailsSympVariable(),
          apiClient.catDetailsSympCT(),
          apiClient.catFrequency(),
          apiClient.catAgeInterval(),
          apiClient.catDuration(),
          apiClient.catDetailsSympOnset(),
          apiClient.catDetailsSympCourse(),
          apiClient.catDetailsSympManifestType(),
        ]);
        const [schemaRes, catDetailsSympVariable, catDetailsSympCT, catFrequency, catAgeInterval, catDuration, catDetailsSympOnset, catDetailsSympCourse, catDetailsSympManifestType] = responses.map((res) => res?.data ?? {});

        if (!schemaRes.formCrud) throw new Error('Estructura de schema inválida.');

        const schema = structuredClone(schemaRes);

        schema.formCrud.ds_variable.items = catDetailsSympVariable.data ?? [];
        schema.formCrud.ds_temporary_concurrence_CT.items = catDetailsSympCT.data ?? [];
        schema.formCrud.ds_permanence_PS.items = catDuration.data ?? [];
        schema.formCrud.ds_onset_IS.items = catDetailsSympOnset.data ?? [];
        schema.formCrud.ds_course_CU.items = catDetailsSympCourse.data ?? [];
        schema.formCrud.ds_duration_DS.items = catDuration.data ?? [];
        schema.formCrud.ds_manifestation_type_TM.items = catDetailsSympManifestType.data ?? [];
        schema.formCrud.ds_frequency_FS.items = catFrequency.data ?? [];
        schema.formCrud.ds_episodes_age_first_EE.items = catAgeInterval.data ?? [];
        schema.formCrud.ds_remission_previous_RP.items = catDuration.data ?? [];
        schema.formCrud.ds_remission_current_RA.items = catDuration.data ?? [];
        
        schema.formCrud2.details_symptoms_ct.items = catDetailsSympVariable.data ?? [];
        schema.formCrud2.ds_ct_permanence_PS.items = catDuration.data ?? [];
        schema.formCrud2.ds_ct_onset_IS.items = catDetailsSympOnset.data ?? [];
        schema.formCrud2.ds_ct_course_CU.items = catDetailsSympCourse.data ?? [];
        schema.formCrud2.ds_ct_manifestation_type_TM.items = catDetailsSympManifestType.data ?? [];
        schema.formCrud2.ds_ct_episodes_age_first_EE.items = catAgeInterval.data ?? [];
        schema.formCrud2.ds_ct_remission_previous_RP.items = catDuration.data ?? [];
        schema.formCrud2.ds_ct_remission_current_RA.items = catDuration.data ?? [];
        commit('SET_SCHEMA', schema);
      } catch (error) {
        handleError(error, commit);
      }
    },

    async fetchRecords({ commit, rootState }, payload) {
      try {
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        const res = await apiClient.modelDSxDetailsSymptoms(clinicalHistories_id, institutions_profes_id, payload);
        const recordsData = res?.data?.data ?? [];
        commit('SET_RECORDS', { recordsData });
        commit('SET_STATUS', 'loaded');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async updateRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'update');
        const { clinicalHistories_id } = getAuthData(rootState);
        const preparedModel = prepareModel(rootState, model);
        await apiClient.updateDSxDetailsSymptoms(clinicalHistories_id, preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async fetchRecordsTrace({ commit, rootState }) {
      try {
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        const res = await apiClient.modelTraceDiag(clinicalHistories_id, institutions_profes_id);
        const recordsData = res?.data?.data ?? [];
        commit('SET_RECORDS_TRACE', { recordsData });
        commit('SET_STATUS', 'loaded');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async traceDiagCreate({ commit, rootState }, { checkboxDS }) {
      try {
        commit('SET_STATUS', 'create');
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        await apiClient.createTraceDiag(clinicalHistories_id, institutions_profes_id, checkboxDS);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async traceDiagUpdate({ commit, rootState }, { checkboxDS }) {
      try {
        commit('SET_STATUS', 'update');
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        await apiClient.updateTraceDiag(clinicalHistories_id, institutions_profes_id, checkboxDS);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },
  },
};
